import { forwardRef } from "react";
import { WaybillContent } from "./waybill-content";
import { TermsAndConditions } from "./terms-and-conditions";
import { TWaybillService } from "~/types/waybill-service";

type InventoryPage = {
  inventories: any[];
  supplies: any[];
  isFirstPage: boolean;
};

type WaybillPages = {
  pages: InventoryPage[];
  totalOddPages: number;
};

type Props = {
  selectedServiceTicketsForPrint: TWaybillService[];
  sortOrder?: "inventoryRef" | "title";
};

export const PrintContent = forwardRef<HTMLDivElement, Props>(
  (
    { selectedServiceTicketsForPrint, sortOrder = "inventoryRef" },
    componentRef
  ) => {
    // Pagination logic for inventory and supplies with sorting
    const createWaybillPages = (ticket: TWaybillService): WaybillPages => {
      let inventories = ticket.inventories || [];
      const supplies = ticket.supplyItems || [];
      // Adjust maxInventoryPerPage based on whether supplies exist
      const maxInventoryPerPage = supplies.length === 0 ? 10 : 5;
      const maxSuppliesPerPage = 5;

      // Sort inventories based on sortOrder
      inventories = [...inventories].sort((a, b) => {
        if (sortOrder === "title") {
          return (a.title || "").localeCompare(b.title || "");
        }
        // Default to inventoryRef
        return (a.inventoryRef || "").localeCompare(b.inventoryRef || "");
      });

      const pages: InventoryPage[] = [];

      // First page: first maxInventoryPerPage inventory items + first 5 supplies
      const firstPageInventories = inventories.slice(0, maxInventoryPerPage);
      const firstPageSupplies = supplies.slice(0, maxSuppliesPerPage);

      pages.push({
        inventories: firstPageInventories,
        supplies: firstPageSupplies,
        isFirstPage: true,
      });

      // Additional pages for remaining inventory and supplies combined
      let remainingInventories = inventories.slice(maxInventoryPerPage);
      let remainingSupplies = supplies.slice(maxSuppliesPerPage);

      // Continue creating pages while there are remaining items
      while (remainingInventories.length > 0 || remainingSupplies.length > 0) {
        const pageInventories = remainingInventories.slice(
          0,
          maxInventoryPerPage
        );
        const pageSupplies = remainingSupplies.slice(0, maxSuppliesPerPage);

        remainingInventories = remainingInventories.slice(maxInventoryPerPage);
        remainingSupplies = remainingSupplies.slice(maxSuppliesPerPage);

        pages.push({
          inventories: pageInventories,
          supplies: pageSupplies,
          isFirstPage: false,
        });
      }

      const totalOddPages = pages.length;

      return { pages, totalOddPages };
    };

    return (
      <>
        <div className="printable-content">
          <div ref={componentRef} className="waybill-container">
            {selectedServiceTicketsForPrint?.length > 0 ? (
              selectedServiceTicketsForPrint?.map((ticket) => {
                const waybillPages = createWaybillPages(ticket);
                const waybillSections: React.ReactElement[] = [];

                waybillPages.pages.forEach((page, pageIndex) => {
                  const contentPageNumber = pageIndex + 1; // Sequential numbering for content pages

                  // Add waybill content page (odd page)
                  waybillSections.push(
                    <WaybillContent
                      key={`${ticket.id}-content-${pageIndex}`}
                      ticket={ticket}
                      inventories={page.inventories}
                      supplies={page.supplies}
                      isFirstPage={page.isFirstPage}
                      pageNumber={contentPageNumber}
                      totalOddPages={waybillPages.totalOddPages}
                    />
                  );

                  // Add terms & conditions page (even page)
                  waybillSections.push(
                    <TermsAndConditions
                      key={`${ticket.id}-terms-${pageIndex}`}
                    />
                  );
                });

                return waybillSections;
              })
            ) : (
              <div>No service tickets selected for printing.</div>
            )}
          </div>
        </div>
        <style>
          {`
          .printable-content {
            position: absolute;
            opacity: 0;
            height: 0;
            overflow: hidden;
          }
          .waybill-container {
            width: 816px; /* Letter width in pixels at 96dpi (8.5in) */
            font-family: Arial, sans-serif;
            font-size: 12px; /* Reduced font size */
            box-sizing: border-box;
            margin: 0 auto;
            position: relative;
          }
          .waybill-page {
            padding: 15px; /* Reduced padding */
            border: 1px solid #000;
            min-height: 1026px; /* Letter height in pixels at 96dpi (11in) minus padding */
            height: 1026px;
            position: relative;
            display: flex;
            flex-direction: column;
          }
          .waybill-grid {
            display: flex;
            flex-direction: column;
            gap: 5px; /* Reduced gap between sections */
            width: 100%;
            height: 100%;
            flex: 1;
          }
          .page-footer {
            margin-top: auto;
            padding-top: 10px;
            border-top: 1px solid #ccc;
            text-align: center;
            font-size: 10px;
            color: #666;
          }
          .terms-page .terms-content {
            flex: 1;
            display: flex;
            flex-direction: column;
          }
          .terms-page .terms-content h3 {
            font-size: 16px;
            font-weight: bold;
            margin-bottom: 15px;
            text-align: center;
            // border-bottom: 2px solid #000;
            padding-bottom: 5px;
          }
          .terms-text {
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 15px;
            font-size: 12px;
            line-height: 1.4;
          }
          .terms-section {
            margin-bottom: 10px;
          }
          .terms-section strong {
            display: block;
            margin-bottom: 5px;
          }
          .terms-footer {
            margin-top: 20px;
            font-weight: bold;
            text-align: center;
            border-top: 1px solid #ccc;
            padding-top: 10px;
          }
          .content-page .waybill-grid {
            gap: 5px;
          }
          .header {
            display: flex;
            justify-content: space-between;
            background-color: #f0f0f0;
            padding: 8px; /* Reduced padding */
            border-bottom: 2px solid #000;
          }
          .header-left h1 {
            font-size: 18px; /* Reduced font size */
            margin: 0 0 3px 0; /* Reduced margin */
          }
          .header-left p {
            margin: 0;
            font-size: 10px; /* Reduced font size */
          }
          .header-right h1 {
            font-size: 18px; /* Reduced font size */
            margin: 0 0 3px 0; /* Reduced margin */
          }
          .header-right p {
            margin: 0;
            font-size: 10px; /* Reduced font size */
          }
          hr {
            margin: 0;
          }
          .client-sections {
            display: flex;
            justify-content: space-between;
            gap: 15px; /* Reduced gap */
            padding-bottom: 5px; /* Reduced padding */
          }
          .booking-client-box, .service-client-box {
            width: 48%;
          }
          .booking-client-box {
            border: 1px solid #000;
            padding: 8px; /* Reduced padding */
          }
          .booking-client-box h3 {
            font-size: 14px; /* Reduced font size */
            margin: 0 0 5px 0; /* Reduced margin */
            border-bottom: 1px solid #ccc;
          }
          .service-client-box .sender-box,
          .service-client-box .receiver-box {
            border: 1px solid #000;
            padding: 8px; /* Reduced padding */
          }
          .sender-box h3, .receiver-box h3 {
            font-size: 14px; /* Reduced font size */
            margin: 0 0 5px 0; /* Reduced margin */
            border-bottom: 1px solid #ccc;
          }
          .service-details {
            margin-top: 5px; /* Reduced margin */
          }
          .service-details h3 {
            font-size: 14px; /* Reduced font size */
            margin-bottom: 5px; /* Reduced margin */
          }
          .service-instructions-box {
            border: 1px solid #000;
            padding: 8px; /* Reduced padding */
          }
          .inventory-items, .supplies, .confirmation {
            margin-top: 5px; /* Reduced margin */
          }
          .inventory-items h3, .supplies h3, .confirmation h3 {
            font-size: 14px; /* Reduced font size */
            margin-bottom: 5px; /* Reduced margin */
          }
          .inventory-items table, .supplies table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 5px; /* Reduced margin */
          }
          .inventory-items th, .inventory-items td,
          .supplies th, .supplies td {
            border: 1px solid #ccc;
            padding: 5px; /* Reduced padding */
            text-align: left;
            font-size: 11px; /* Reduced font size */
          }
          .inventory-items th, .supplies th {
            background-color: #f0f0f0;
            font-weight: bold;
          }
          .confirmation-box {
            border: 1px solid #000;
            padding: 8px; /* Reduced padding */
          }
          .confirmation-box p {
            margin: 3px 0; /* Reduced margin */
          }
          .signature-placeholder {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%;
            min-height: 50px;
            margin: 8px 0;
            font-size: 16px;
            text-align: center;
            color: #999;
          }
          .content-page .waybill-grid {
            display: grid;
            grid-template-rows: 100px auto auto 1fr auto auto;
            row-gap: 15px;
            height: calc(100% - 20px);
          }

          .signature-line {
            text-align: center;
            margin: 20px 0 5px;
            font-size: 16px;
          }
          @media print {
            @page {
              size: letter;
              margin: 10mm;
            }
            .printable-content {
              position: static;
              opacity: 1;
              height: auto;
              overflow: visible;
            }
            .waybill-container {
              width: 816px; /* Letter width in pixels at 96dpi (8.5in) */
              height: 1056px; /* Letter height in pixels at 96dpi (11in) */
              font-family: Arial, sans-serif;
              font-size: 12px; /* Reduced font size */
              box-sizing: border-box;
              margin: 0 auto;
              position: relative;
            }
            .waybill-page {
              padding: 15px; /* Reduced padding */
              border: 1px solid #000;
              page-break-after: always; /* Force a page break after each page */
              min-height: 1026px; /* Letter height in pixels at 96dpi (11in) minus padding */
              height: 1056px;
              position: relative;
              display: flex;
              flex-direction: column;
            }
            .waybill-page:last-child {
              page-break-after: avoid; /* Avoid a page break after the last page */
            }
            .waybill-grid {
              display: flex;
              flex-direction: column;
              gap: 5px; /* Reduced gap */
              width: 100%;
              height: 100%;
              flex: 1;
            }
            .page-footer {
              margin-top: auto;
              padding-top: 10px;
              // border-top: 1px solid #ccc;
              text-align: center;
              font-size: 10px;
              color: #666;
            }
            .terms-page .terms-content {
              flex: 1;
              display: flex;
              flex-direction: column;
              min-height: 0; /* Allow flex item to shrink */
            }
            .terms-page .terms-content h3 {
              font-size: 16px;
              font-weight: bold;
              margin-bottom: 15px;
              text-align: center;
              // border-bottom: 2px solid #000;
              padding-bottom: 5px;
              flex-shrink: 0; /* Prevent title from shrinking */
            }
            .terms-text {
              flex: 1;
              display: flex;
              flex-direction: column;
              gap: 15px;
              font-size: 12px;
              line-height: 1.4;
              min-height: 0; /* Allow flex item to shrink */
            }
            .terms-section {
              margin-bottom: 10px;
            }
            .terms-section strong {
              display: block;
              margin-bottom: 5px;
            }
            .terms-footer {
              // margin-top: auto;
              font-weight: bold;
              text-align: center;
              border-top: 1px solid #ccc;
              padding-top: 10px;
              flex-shrink: 0; /* Prevent footer from shrinking */
            }
            .content-page .waybill-grid {
              gap: 5px;
            }
            .header {
              display: flex;
              justify-content: space-between;
              background-color: #f0f0f0;
              padding: 8px; /* Reduced padding */
              border-bottom: 2px solid #000;
            }
            .header-left h1 {
              font-size: 18px; /* Reduced font size */
              margin: 0 0 3px 0; /* Reduced margin */
            }
            .header-left p {
              margin: 0;
              font-size: 10px; /* Reduced font size */
            }
            .header-right h1 {
              font-size: 18px; /* Reduced font size */
              margin: 0 0 3px 0; /* Reduced margin */
            }
            .header-right p {
              margin: 0;
              font-size: 10px; /* Reduced font size */
            }
            hr {
              margin: 0;
            }
            .client-sections {
              display: flex;
              justify-content: space-between;
              gap: 15px; /* Reduced gap */
              padding-bottom: 5px; /* Reduced padding */
            }
            .booking-client-box, .service-client-box {
              width: 48%;
            }
            .booking-client-box {
              border: 1px solid #000;
              padding: 8px; /* Reduced padding */
            }
            .booking-client-box h3 {
              font-size: 14px; /* Reduced font size */
              margin: 0 0 5px 0; /* Reduced margin */
              border-bottom: 1px solid #ccc;
            }
            .service-client-box .sender-box,
            .service-client-box .receiver-box {
              border: 1px solid #000;
              padding: 8px; /* Reduced padding */
            }
            .sender-box h3, .receiver-box h3 {
              font-size: 14px; /* Reduced font size */
              margin: 0 0 5px 0; /* Reduced margin */
              border-bottom: 1px solid #ccc;
            }
            .service-details {
              margin-top: 5px; /* Reduced margin */
            }
            .service-details h3 {
              font-size: 14px; /* Reduced font size */
              margin-bottom: 5px; /* Reduced margin */
            }
            .service-instructions-box {
              border: 1px solid #000;
              padding: 8px; /* Reduced padding */
            }
            .inventory-items, .supplies, .confirmation {
              margin-top: 5px; /* Reduced margin */
            }
            .inventory-items h3, .supplies h3, .confirmation h3 {
              font-size: 14px; /* Reduced font size */
              margin-bottom: 5px; /* Reduced margin */
            }
            .inventory-items table, .supplies table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 5px; /* Reduced margin */
            }
            .inventory-items th, .inventory-items td,
            .supplies th, .supplies td {
              border: 1px solid #ccc;
              padding: 5px; /* Reduced padding */
              text-align: left;
              font-size: 11px; /* Reduced font size */
            }
            .inventory-items th, .supplies th {
              background-color: #f0f0f0;
              font-weight: bold;
            }
            .confirmation-box {
              border: 1px solid #000;
              padding: 8px; /* Reduced padding */
            }
            .confirmation-box p {
              margin: 3px 0; /* Reduced margin */
            }
          }
        `}
        </style>
      </>
    );
  }
);
